/**
 * Data mappers for converting GraphQL BillingStats to chart data formats
 * used by the billing dashboard.
 */

import type {
  BillingPeriodStatGraphQL,
  BillingBucketGraphQL,
  BillingStatsGraphQL,
} from './useBillingStatsRelay';

// ============================================================================
// Types
// ============================================================================

export interface ChartDataPoint {
  x: number | string;
  y: number;
  label: number;
}

export interface DayChartResult {
  data: ChartDataPoint[];
  total: number;
  average: number;
  tickValues: number[];
}

export interface MonthChartResult {
  data: ChartDataPoint[];
  total: number;
  average: number;
}

// ============================================================================
// Period Stats Mappers
// ============================================================================

/**
 * Map period stats to daily chart data (DAY granularity).
 * Returns chart data with x = day number (1-31), y/label = dollars.
 * Total and average are returned in cents for use with convertCentsToMoney.
 */
export function mapPeriodStatsToDayChartData(
  stats: BillingPeriodStatGraphQL[] | null,
  month: number,
  year: number
): DayChartResult {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const tickValues: number[] = [];
  const dataArray: ChartDataPoint[] = [];

  for (let day = 1; day <= daysInMonth; day++) {
    tickValues.push(day);
    dataArray.push({ x: day, y: 0, label: 0 });
  }

  let total = 0;

  if (stats) {
    stats.forEach((stat) => {
      const date = new Date(stat.period);
      const day = date.getUTCDate();
      if (day >= 1 && day <= daysInMonth) {
        const dollars = parseFloat((stat.totalAmount / 100).toFixed(2));
        dataArray[day - 1].y = dollars;
        dataArray[day - 1].label = parseFloat(dollars.toFixed(0));
        total += stat.totalAmount;
      }
    });
  }

  const emptyCheck = dataArray.every((el) => el.y === 0);

  let average = total / daysInMonth;
  const now = new Date();
  if (month === now.getUTCMonth() && year === now.getUTCFullYear()) {
    average = total / now.getUTCDate();
  }

  return {
    data: emptyCheck ? [] : dataArray,
    total,
    average,
    tickValues,
  };
}

/**
 * Map period stats to monthly chart data (MONTH granularity).
 * Returns chart data with x = month index (0-11), y/label = dollars.
 * Total and average are returned in cents for use with convertCentsToMoney.
 */
export function mapPeriodStatsToMonthChartData(
  stats: BillingPeriodStatGraphQL[] | null,
  year: number
): MonthChartResult {
  const dataArray: ChartDataPoint[] = [];

  for (let month = 0; month < 12; month++) {
    dataArray.push({ x: month, y: 0, label: 0 });
  }

  let total = 0;

  if (stats) {
    stats.forEach((stat) => {
      const date = new Date(stat.period);
      const month = date.getUTCMonth();
      if (month >= 0 && month < 12) {
        const dollars = parseFloat((stat.totalAmount / 100).toFixed(2));
        dataArray[month].y = dollars;
        dataArray[month].label = parseFloat(dollars.toFixed(0));
        total += stat.totalAmount;
      }
    });
  }

  const emptyCheck = dataArray.every((el) => el.y === 0);

  let average = total / 12;
  if (year === new Date().getUTCFullYear()) {
    average = total / (new Date().getUTCMonth() + 1);
  }

  return {
    data: emptyCheck ? [] : dataArray,
    total,
    average,
  };
}

// ============================================================================
// Distribution Mappers
// ============================================================================

/**
 * Map billing buckets to charge distribution chart data.
 * Returns chart data with x = bucket label, y/label = count.
 */
export function mapBucketsToChartData(
  buckets: BillingBucketGraphQL[] | null
): ChartDataPoint[] {
  if (!buckets || buckets.length === 0) return [];

  const data = buckets.map((bucket) => ({
    x: bucket.label,
    y: bucket.count,
    label: bucket.count,
  }));

  const emptyCheck = data.every((el) => el.y === 0);
  return emptyCheck ? [] : data;
}

/**
 * Get distribution labels from buckets for chart axis.
 */
export function getDistributionLabels(
  buckets: BillingBucketGraphQL[] | null
): string[] {
  if (!buckets || buckets.length === 0) return [];
  return buckets.map((bucket) => bucket.label);
}

// ============================================================================
// Quick Stats Mappers
// ============================================================================

/**
 * Map quick stats from billingStats response.
 * averageCharge/averageRefund are formatted as dollar strings.
 */
export function mapQuickStats(stats: BillingStatsGraphQL | null): {
  averageCharge: string;
  averageRefund: string;
  chargeCount: number;
  refundCount: number;
} {
  if (!stats) {
    return {
      averageCharge: '0',
      averageRefund: '0',
      chargeCount: 0,
      refundCount: 0,
    };
  }

  return {
    averageCharge: stats.averageChargeAmount
      ? (stats.averageChargeAmount / 100).toFixed(2)
      : '0',
    averageRefund: stats.averageRefundAmount
      ? (stats.averageRefundAmount / 100).toFixed(2)
      : '0',
    chargeCount: stats.totalChargeCount ?? 0,
    refundCount: stats.totalRefundCount ?? 0,
  };
}

/**
 * Compute distribution stats (total rentals and rental average in cents)
 * from billing stats for the "Rentals by Charge" chart header.
 */
export function mapDistributionStats(stats: BillingStatsGraphQL | null): {
  totalRentals: number;
  rentalAverage: string;
} {
  if (!stats) {
    return { totalRentals: 0, rentalAverage: '0' };
  }

  const totalRentals = stats.totalChargeCount ?? 0;
  const totalAmount = stats.totalChargeAmount ?? 0;
  const average =
    totalRentals > 0 ? (totalAmount / totalRentals).toFixed(0) : '0';

  return { totalRentals, rentalAverage: average };
}
